/**
 * Settings panel — a gear button (top-right, beside the "?" help button) that
 * toggles a center modal with master mute + per-category volume sliders
 * (Music / Ambience / Effects / Interface / Voice).
 *
 * Pure UI: it reads and writes the AudioManager, which owns the live bus gains
 * and persists them (+ master mute) to localStorage, so settings survive a
 * reload and apply to in-flight music / ambience loops immediately. Mirrors the
 * help-panel factory shape ({ open, close, isOpen, toggle }) so main.ts can fold
 * it into the Esc-to-close chain. Created at boot so it works on the menu (menu
 * music volume) as well as in-match.
 */
import type { AudioManager } from "../audio/audio-manager";
import type { AudioBus } from "@tribez/shared";
import {
  getCameraSpeedMultiplier,
  setCameraSpeedMultiplier,
  CAMERA_SPEED_MIN,
  CAMERA_SPEED_MAX,
} from "./camera-settings";
import { getShowFps, setShowFps } from "./debug-overlay-settings";
import { getEdgePanEnabled, setEdgePanEnabled } from "./edge-pan-settings";
import {
  getQualityTier,
  setQualityTier,
  QUALITY_TIERS,
} from "./resolution-settings";

export interface SettingsPanel {
  toggle(): void;
  open(): void;
  close(): void;
  isOpen(): boolean;
}

/** Player-facing category labels per audio bus, in display order. */
const BUS_ROWS: ReadonlyArray<{ bus: AudioBus; label: string }> = [
  { bus: "music", label: "Music" },
  { bus: "ambience", label: "Ambience" },
  { bus: "sfx", label: "Effects" },
  { bus: "ui", label: "Interface" },
  { bus: "voice", label: "Voice" },
];

export function createSettingsPanel(
  getAudio: () => AudioManager | null,
  /** Re-apply the resolution scale to the live renderer (main.ts owns the renderer
   *  and calls renderer.setPixelRatio(effectivePixelRatio())). Called on release. */
  onResolutionChange?: () => void,
): SettingsPanel {
  const btn = document.createElement("button");
  btn.id = "settings-btn";
  btn.type = "button";
  btn.textContent = "⚙";
  btn.title = "Settings";
  document.body.appendChild(btn);

  const modal = document.createElement("div");
  modal.id = "settings-modal";
  modal.style.display = "none";

  const rowsHtml = BUS_ROWS.map(
    (r) => `
      <div class="settings-row">
        <label class="settings-row__label" for="settings-vol-${r.bus}">${r.label}</label>
        <input class="settings-row__slider" id="settings-vol-${r.bus}" type="range" min="0" max="100" step="1" data-bus="${r.bus}" />
        <span class="settings-row__val" data-bus="${r.bus}">0%</span>
      </div>`,
  ).join("");

  // Static template (labels are module constants) — no untrusted content.
  modal.innerHTML = `
    <div class="settings-modal__hdr">
      <span>Settings</span>
      <span class="settings-modal__close">[Esc] close</span>
    </div>
    <div class="settings-modal__body">
      <div class="settings-section__title">Audio</div>
      <label class="settings-mute">
        <input type="checkbox" id="settings-master-mute" />
        <span>Mute all</span>
      </label>
      ${rowsHtml}
      <div class="settings-section__title">Controls</div>
      <div class="settings-row">
        <label class="settings-row__label" for="settings-cam-speed">Camera Speed</label>
        <input class="settings-row__slider" id="settings-cam-speed" type="range"
               min="${Math.round(CAMERA_SPEED_MIN * 100)}" max="${Math.round(CAMERA_SPEED_MAX * 100)}" step="5" />
        <span class="settings-row__val" id="settings-cam-speed-val">100%</span>
      </div>
      <label class="settings-mute">
        <input type="checkbox" id="settings-edge-pan" />
        <span>Edge scrolling (pan camera at screen edges)</span>
      </label>
      <div class="settings-section__title">Display</div>
      <div class="settings-row">
        <span class="settings-row__label">Graphics</span>
        <div class="settings-seg" id="settings-quality">
          ${QUALITY_TIERS.map(
            (q) => `<button type="button" class="settings-seg__btn" data-tier="${q.tier}">${q.label}</button>`,
          ).join("")}
        </div>
      </div>
      <label class="settings-mute">
        <input type="checkbox" id="settings-show-fps" />
        <span>Show FPS &amp; ping</span>
      </label>
    </div>
  `;
  document.body.appendChild(modal);

  const camSpeedEl = modal.querySelector<HTMLInputElement>("#settings-cam-speed")!;
  const camSpeedValEl = modal.querySelector<HTMLSpanElement>("#settings-cam-speed-val")!;
  camSpeedEl.addEventListener("input", () => {
    const pct = Number(camSpeedEl.value);
    camSpeedValEl.textContent = `${pct}%`;
    setCameraSpeedMultiplier(pct / 100);
  });

  const qualityEl = modal.querySelector<HTMLDivElement>("#settings-quality")!;
  const qualityBtns = Array.from(qualityEl.querySelectorAll<HTMLButtonElement>(".settings-seg__btn"));
  const syncQualityButtons = () => {
    const cur = getQualityTier();
    for (const b of qualityBtns) b.classList.toggle("is-active", b.dataset.tier === cur);
  };
  for (const b of qualityBtns) {
    b.addEventListener("click", () => {
      const tier = b.dataset.tier;
      if (tier !== "low" && tier !== "medium" && tier !== "high") return;
      setQualityTier(tier);
      syncQualityButtons();
      onResolutionChange?.();
    });
  }

  const showFpsEl = modal.querySelector<HTMLInputElement>("#settings-show-fps")!;
  showFpsEl.addEventListener("change", () => setShowFps(showFpsEl.checked));

  const edgePanEl = modal.querySelector<HTMLInputElement>("#settings-edge-pan")!;
  edgePanEl.addEventListener("change", () => setEdgePanEnabled(edgePanEl.checked));

  const muteEl = modal.querySelector<HTMLInputElement>("#settings-master-mute")!;
  const sliderEls = new Map<AudioBus, HTMLInputElement>();
  const valEls = new Map<AudioBus, HTMLSpanElement>();
  for (const r of BUS_ROWS) {
    sliderEls.set(r.bus, modal.querySelector<HTMLInputElement>(`#settings-vol-${r.bus}`)!);
    valEls.set(r.bus, modal.querySelector<HTMLSpanElement>(`.settings-row__val[data-bus="${r.bus}"]`)!);
  }

  /** Mirror the live AudioManager state into the controls (called on open so a
   *  reload / another tab's change is reflected). */
  function syncFromAudio(): void {
    const audio = getAudio();
    muteEl.checked = audio?.isMasterMuted() ?? false;
    for (const r of BUS_ROWS) {
      const pct = Math.round((audio ? audio.getBusVolume(r.bus) : 0) * 100);
      sliderEls.get(r.bus)!.value = String(pct);
      valEls.get(r.bus)!.textContent = `${pct}%`;
    }
    const camPct = Math.round(getCameraSpeedMultiplier() * 100);
    camSpeedEl.value = String(camPct);
    camSpeedValEl.textContent = `${camPct}%`;
    syncQualityButtons();
    showFpsEl.checked = getShowFps();
    edgePanEl.checked = getEdgePanEnabled();
  }

  for (const r of BUS_ROWS) {
    const slider = sliderEls.get(r.bus)!;
    slider.addEventListener("input", () => {
      const pct = Number(slider.value);
      valEls.get(r.bus)!.textContent = `${pct}%`;
      getAudio()?.setBusVolume(r.bus, pct / 100);
    });
  }
  muteEl.addEventListener("change", () => {
    getAudio()?.setMasterMute(muteEl.checked);
  });

  let opened = false;
  function open(): void { opened = true; syncFromAudio(); modal.style.display = "flex"; }
  function close(): void { opened = false; modal.style.display = "none"; }
  function toggle(): void { if (opened) close(); else open(); }

  btn.addEventListener("click", toggle);
  modal.querySelector<HTMLSpanElement>(".settings-modal__close")!.addEventListener("click", close);

  return { toggle, open, close, isOpen: () => opened };
}
